import { useState, createContext, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, NavLink, useLocation, Navigate } from "react-router-dom";
import { AnimatedNotificationBar } from "@/components/AnimatedNotificationBar";
import { useTranslation } from 'react-i18next';
import '@/i18n';
import { safeLocalStorage } from "@/utils/safeStorage";
import { 
  LayoutDashboard, 
  Users, 
  FolderKanban, 
  Tags, 
  Settings, 
  Download,
  Bot,
  Menu,
  X,
  Globe,
  BookOpen,
  CreditCard,
  Shield,
  LogOut,
  LogIn,
  UserPlus,
  Megaphone,
  Sun,
  Moon,
  Languages,
  ChevronDown,
  Check,
  Gift
} from "lucide-react";

// Pages
import Dashboard from "@/pages/Dashboard";
import Groups from "@/pages/Groups";
import Members from "@/pages/Members";
import Categories from "@/pages/Categories";
import SettingsPage from "@/pages/Settings";
import Export from "@/pages/Export";
import Platforms from "@/pages/Platforms";
import Guide from "@/pages/Guide";
import Subscription from "@/pages/Subscription";
import AdminDashboard from "@/pages/AdminDashboard";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Marketing from "@/pages/marketing/Marketing";
import LandingPage from "@/pages/LandingPage";
import OeiaWebsite from "@/pages/OeiaWebsite";
import ReferralProgram from "@/pages/ReferralProgram";
import PluckLanding from "@/pages/PluckLanding";
import ComingSoon from "@/pages/ComingSoon";

// Utilities
import { initReferralTracking } from "@/utils/referralTracking";

// Auth Context
import { AuthProvider, useAuth } from "@/contexts/AuthContext";

// Theme Context
import { ThemeProvider, useTheme } from "@/contexts/ThemeContext";

// Language Context
import { LanguageProvider, useLanguage } from "@/contexts/LanguageContext";

// Notifications
import useNotifications from "@/hooks/useNotifications";
import { NotificationPopup, NotificationToggle } from "@/components/NotificationSystem";
import { notificationService } from "@/services/notificationService";

// Create context for notifications
export const NotificationContext = createContext(null);

// Theme Toggle Component - Enhanced & Eye-catching
const ThemeToggle = () => {
  const { theme, toggleTheme, isDark } = useTheme();
  const { t } = useTranslation();
  
  return (
    <button
      onClick={toggleTheme}
      data-testid="theme-toggle"
      className="relative w-16 h-8 rounded-full transition-all duration-500 flex items-center overflow-hidden"
      style={{
        background: isDark 
          ? 'linear-gradient(135deg, #0F172A 0%, #1E293B 50%, #334155 100%)' 
          : 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 50%, #FCD34D 100%)',
        border: isDark ? '2px solid rgba(99, 102, 241, 0.5)' : '2px solid #F59E0B',
        boxShadow: isDark 
          ? '0 0 20px rgba(99, 102, 241, 0.4), inset 0 0 15px rgba(99, 102, 241, 0.2)' 
          : '0 0 20px rgba(245, 158, 11, 0.4), inset 0 0 15px rgba(245, 158, 11, 0.2)'
      }}
      title={isDark ? t('theme.light') : t('theme.dark')}
    >
      {/* Stars for dark mode */}
      {isDark && (
        <>
          <span className="absolute w-1 h-1 bg-white rounded-full animate-pulse" style={{ top: '6px', left: '8px', opacity: 0.8 }}></span>
          <span className="absolute w-0.5 h-0.5 bg-white rounded-full animate-pulse" style={{ top: '14px', left: '14px', opacity: 0.6, animationDelay: '0.3s' }}></span>
          <span className="absolute w-1 h-1 bg-white rounded-full animate-pulse" style={{ top: '20px', left: '6px', opacity: 0.7, animationDelay: '0.6s' }}></span>
        </>
      )}
      
      {/* Sun rays for light mode */}
      {!isDark && (
        <>
          <span className="absolute w-6 h-6 rounded-full animate-ping" style={{ right: '18px', top: '4px', background: 'rgba(251, 191, 36, 0.3)' }}></span>
        </>
      )}
      
      <div
        className="absolute w-6 h-6 rounded-full transition-all duration-500 flex items-center justify-center z-10"
        style={{
          right: isDark ? '4px' : 'auto',
          left: isDark ? 'auto' : '4px',
          background: isDark 
            ? 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)' 
            : 'linear-gradient(135deg, #FBBF24 0%, #F59E0B 100%)',
          boxShadow: isDark 
            ? '0 0 15px rgba(99, 102, 241, 0.8), 0 2px 10px rgba(0,0,0,0.3)' 
            : '0 0 15px rgba(245, 158, 11, 0.8), 0 2px 10px rgba(0,0,0,0.2)'
        }}
      >
        {isDark ? (
          <Moon size={14} className="text-white" />
        ) : (
          <Sun size={14} className="text-amber-900" />
        )}
      </div>
    </button>
  );
};

// Language Toggle Component - Professional Dropdown
const LanguageToggle = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { language, setLanguage, isArabic } = useLanguage();
  const { t } = useTranslation();
  
  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸', nativeName: 'English' },
    { code: 'ar', name: 'العربية', flag: '🇸🇦', nativeName: 'Arabic' }
  ];
  
  const currentLang = languages.find(l => l.code === language) || languages[0];
  
  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        data-testid="language-toggle"
        className="flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-300 hover:bg-white/10 border border-white/10 hover:border-white/20"
        title={t('language.selectLanguage')}
      >
        <Globe size={16} className="text-cyan-400" />
        <span className="text-sm font-medium text-white">{currentLang.flag} {currentLang.code.toUpperCase()}</span>
        <ChevronDown size={14} className={`text-neutral-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {/* Dropdown Menu */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Menu */}
          <div className="absolute top-full mt-2 right-0 z-50 min-w-[160px] py-2 rounded-xl glass border border-white/10 shadow-xl shadow-black/20 animate-fade-in">
            <div className="px-3 py-2 border-b border-white/10">
              <p className="text-xs text-neutral-500 uppercase tracking-wider">{t('language.selectLanguage')}</p>
            </div>
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => {
                  setLanguage(lang.code);
                  setIsOpen(false);
                }}
                data-testid={`lang-${lang.code}`}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-all duration-200 hover:bg-white/10 ${
                  language === lang.code ? 'bg-cyan-500/10 text-cyan-400' : 'text-white'
                }`}
              >
                <span className="text-lg">{lang.flag}</span>
                <div>
                  <p className="text-sm font-medium">{lang.name}</p>
                  <p className="text-xs text-neutral-500">{lang.nativeName}</p>
                </div>
                {language === lang.code && (
                  <Check size={14} className="text-cyan-400 ml-auto" />
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading, isAdmin, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && !isAdmin()) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const Sidebar = ({ soundEnabled, onToggleSound }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [counts, setCounts] = useState({ groups: 0, members: 0 });
  const location = useLocation();
  const { user, logout, isAdmin, isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const { isRTL } = useLanguage();

  // جلب أعداد المجموعات والأعضاء
  useEffect(() => {
    const fetchCounts = async () => {
      if (!isAuthenticated()) return;
      
      try {
        const token = safeLocalStorage.getItem('auth_token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        
        const [groupsRes, membersRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/groups/count`, { headers }),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/members/count`, { headers })
        ]);
        
        if (groupsRes.ok && membersRes.ok) {
          const groupsData = await groupsRes.json();
          const membersData = await membersRes.json();
          setCounts({
            groups: groupsData.count || 0,
            members: membersData.count || 0
          });
        }
      } catch (error) {
        console.error('Error fetching counts:', error);
      }
    };
    
    fetchCounts();
    
    // تحديث كل 10 ثوانٍ
    const interval = setInterval(fetchCounts, 10000);
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  // Navigation items - لوحة المسؤول تظهر فقط للمسؤول
  const navItems = [
    { path: "/", icon: LayoutDashboard, label: t('nav.dashboard'), requiresAuth: true },
    { path: "/platforms", icon: Globe, label: t('nav.platforms'), requiresAuth: true },
    { path: "/guide", icon: BookOpen, label: t('nav.guide'), requiresAuth: true },
    { path: "/groups", icon: FolderKanban, label: t('nav.groups'), requiresAuth: true, count: counts.groups },
    { path: "/members", icon: Users, label: t('nav.members'), requiresAuth: true, count: counts.members },
    { path: "/categories", icon: Tags, label: t('nav.categories'), requiresAuth: true },
    { path: "/export", icon: Download, label: t('nav.export'), requiresAuth: true },
    { path: "/subscription", icon: CreditCard, label: t('nav.subscription'), requiresAuth: true },
    { path: "/referral", icon: Gift, label: t('nav.referral'), requiresAuth: true, highlight: true },
    { path: "/settings", icon: Settings, label: t('nav.settings'), requiresAuth: true },
    { path: "/marketing", icon: Megaphone, label: t('nav.marketing'), requiresAuth: true, highlight: true },
  ];

  // إضافة لوحة المسؤول فقط للمسؤولين
  if (isAdmin()) {
    navItems.push({ path: "/admin", icon: Shield, label: t('nav.admin'), adminOnly: true });
  }

  // Auth items for non-authenticated users
  const authItems = [
    { path: "/login", icon: LogIn, label: t('nav.login') },
    { path: "/register", icon: UserPlus, label: t('nav.register') },
  ];

  const handleLogout = () => {
    logout();
    setIsOpen(false);
  };

  return (
    <>
      {/* Mobile menu button */}
      <button
        data-testid="mobile-menu-btn"
        className={`lg:hidden fixed top-4 ${isRTL ? 'right-4' : 'left-4'} z-50 p-2 rounded-lg glass`}
        onClick={() => setIsOpen(!isOpen)}
      >
        {isOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Sidebar */}
      <aside 
        data-testid="sidebar"
        className={`sidebar ${isOpen ? 'translate-x-0' : (isRTL ? 'translate-x-full lg:translate-x-0' : '-translate-x-full lg:translate-x-0')} transition-transform duration-300`}
      >
        {/* Logo */}
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500 to-cyan-600 flex items-center justify-center neon-glow">
                <Bot size={24} className="text-black" />
              </div>
              <div>
                <h1 className="font-bold text-lg">{t('app.name')}</h1>
                <p className="text-xs text-neutral-500">{t('app.description')}</p>
              </div>
            </div>
          </div>
          {/* Controls Row */}
          <div className="flex items-center gap-2 mt-4">
            {/* Language Toggle */}
            <LanguageToggle />
            {/* Theme Toggle */}
            <ThemeToggle />
            {/* Notification Toggle - Only for authenticated users */}
            {isAuthenticated() && (
              <NotificationToggle 
                soundEnabled={soundEnabled} 
                onToggle={onToggleSound} 
              />
            )}
          </div>
        </div>

        {/* User Info - If authenticated */}
        {isAuthenticated() && user && (
          <div className="p-4 border-b border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-violet-600 flex items-center justify-center text-white font-bold">
                {user.name?.charAt(0) || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold truncate">{user.name || t('common.name')}</p>
                <p className="text-xs text-neutral-500 truncate">{user.email}</p>
                {isAdmin() && (
                  <span className="badge badge-error text-xs mt-1">{t('nav.admin')}</span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="p-4 space-y-1 flex-1 overflow-y-auto">
          {isAuthenticated() ? (
            // Authenticated navigation
            <>
              {navItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.path.replace('/', '') || 'dashboard'}`}
                  onClick={() => setIsOpen(false)}
                  className={({ isActive }) =>
                    `sidebar-item flex items-center gap-3 px-4 py-3 rounded-lg ${
                      isActive ? 'active' : 'text-neutral-400'
                    } ${item.adminOnly ? 'bg-red-500/10 border border-red-500/20' : ''} ${
                      item.highlight ? 'bg-emerald-500/10 border border-emerald-500/30 hover:bg-emerald-500/20' : ''
                    }`
                  }
                >
                  <item.icon size={20} className={item.adminOnly ? 'text-red-400' : item.highlight ? 'text-emerald-400' : ''} />
                  <span className={item.adminOnly ? 'text-red-400' : item.highlight ? 'text-emerald-400 font-semibold' : ''}>{item.label}</span>
                  {/* عداد رقمي للمجموعات والأعضاء */}
                  {item.count !== undefined && item.count > 0 && (
                    <span className={`${isRTL ? 'mr-auto' : 'ml-auto'} min-w-[24px] h-6 flex items-center justify-center text-xs font-bold rounded-full ${
                      item.path === '/members' 
                        ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white' 
                        : 'bg-gradient-to-r from-violet-500 to-purple-500 text-white'
                    } animate-pulse`}>
                      {item.count > 999 ? '999+' : item.count}
                    </span>
                  )}
                  {item.highlight && !item.count && (
                    <span className={`${isRTL ? 'mr-auto' : 'ml-auto'} text-[10px] bg-emerald-500 text-black px-1.5 py-0.5 rounded-full font-bold`}>{t('common.new')}</span>
                  )}
                </NavLink>
              ))}
              
              {/* Logout Button */}
              <button
                onClick={handleLogout}
                data-testid="logout-btn"
                className="sidebar-item flex items-center gap-3 px-4 py-3 rounded-lg text-red-400 hover:bg-red-500/10 w-full mt-4"
              >
                <LogOut size={20} />
                <span>{t('nav.logout')}</span>
              </button>
            </>
          ) : (
            // Non-authenticated navigation
            authItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.path.replace('/', '')}`}
                onClick={() => setIsOpen(false)}
                className={({ isActive }) =>
                  `sidebar-item flex items-center gap-3 px-4 py-3 rounded-lg ${
                    isActive ? 'active' : 'text-neutral-400'
                  }`
                }
              >
                <item.icon size={20} />
                <span>{item.label}</span>
              </NavLink>
            ))
          )}
        </nav>

        {/* Bot Status - Only for authenticated users */}
        {isAuthenticated() && (
          <div className="p-4 border-t border-white/10">
            <div className="glass rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="status-offline" data-testid="bot-status-indicator"></div>
                <span className="text-sm text-neutral-400">{t('botStatus.title')}</span>
              </div>
              <p className="text-xs text-neutral-500">
                {t('botStatus.addTelegramApi')}
              </p>
            </div>
          </div>
        )}
      </aside>
    </>
  );
};

// Main Layout with Sidebar
const MainLayout = ({ children, notifications }) => {
  const { isRTL } = useLanguage();
  
  return (
    <div className="app-container" dir={isRTL ? 'rtl' : 'ltr'}>
      <Sidebar 
        soundEnabled={notifications.soundEnabled} 
        onToggleSound={notifications.toggleSound} 
      />
      <main className="main-content">
        {children}
      </main>
      <AnimatedNotificationBar isRTL={isRTL} />
    </div>
  );
};

// Auth Layout (Login/Register pages without sidebar)
const AuthLayout = ({ children }) => {
  const { isRTL } = useLanguage();
  
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#5D6658' }} dir={isRTL ? 'rtl' : 'ltr'}>
      {children}
      <AnimatedNotificationBar isRTL={isRTL} />
    </div>
  );
};

function AppContent() {
  const notifications = useNotifications();
  const { isAuthenticated } = useAuth();

  // MAINTENANCE MODE - Read from environment variable
  const isMaintenanceMode = process.env.REACT_APP_MAINTENANCE_MODE === 'true';
  
  // Check if staff bypass is enabled (allows access to login page)
  // Wrapped in try-catch for Edge browser compatibility
  let hasMaintenanceBypass = false;
  try {
    if (typeof window !== 'undefined') {
      // Check URL parameter first (Edge fallback)
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('staff_bypass') === 'true') {
        hasMaintenanceBypass = true;
      }
      // Then check sessionStorage
      if (window.sessionStorage) {
        hasMaintenanceBypass = hasMaintenanceBypass || sessionStorage.getItem('maintenance_bypass') === 'true';
      }
    }
  } catch (e) {
    // sessionStorage not available (Edge privacy settings)
    // Check URL parameter as fallback
    try {
      const urlParams = new URLSearchParams(window.location.search);
      hasMaintenanceBypass = urlParams.get('staff_bypass') === 'true';
    } catch (e2) {
      hasMaintenanceBypass = false;
    }
  }

  // Cache authentication status to prevent re-renders
  const isUserAuthenticated = isAuthenticated();

  // بدء خدمة الإشعارات عند تسجيل الدخول
  useEffect(() => {
    if (isUserAuthenticated) {
      notificationService.start();
      // Clear bypass once authenticated
      try {
        sessionStorage.removeItem('maintenance_bypass');
      } catch (e) {
        // Ignore if sessionStorage not available
      }
    } else {
      notificationService.stop();
    }
    
    return () => {
      notificationService.stop();
    };
  }, [isUserAuthenticated]);

  // If maintenance mode is enabled, show Coming Soon page for non-authenticated users
  // unless they have the maintenance bypass (for staff login access)
  if (isMaintenanceMode && !isUserAuthenticated && !hasMaintenanceBypass) {
    return <ComingSoon />;
  }

  return (
    <NotificationContext.Provider value={notifications}>
      <BrowserRouter>
        <Routes>
          {/* Root route - Redirect to /home for non-authenticated users */}
          <Route path="/" element={
            isAuthenticated() ? (
              <MainLayout notifications={notifications}>
                <Dashboard />
              </MainLayout>
            ) : (
              <PluckLanding />
            )
          } />
          
          {/* oeia.io Website - Public */}
          <Route path="/website" element={
            <OeiaWebsite />
          } />
          
          {/* New Pluck Landing Page - Public */}
          <Route path="/home" element={
            <PluckLanding />
          } />
          
          {/* Landing Page - Public */}
          <Route path="/landing" element={
            <LandingPage />
          } />
          
          {/* Auth Routes - No Sidebar */}
          <Route path="/login" element={
            isAuthenticated() ? <Navigate to="/" replace /> : (
              <AuthLayout>
                <Login />
              </AuthLayout>
            )
          } />
          <Route path="/register" element={
            isAuthenticated() ? <Navigate to="/" replace /> : (
              <AuthLayout>
                <Register />
              </AuthLayout>
            )
          } />
          
          {/* Protected Routes - With Sidebar */}
          <Route path="/*" element={
            <MainLayout notifications={notifications}>
              <Routes>
                <Route path="/dashboard" element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                } />
                <Route path="/platforms" element={
                  <ProtectedRoute>
                    <Platforms />
                  </ProtectedRoute>
                } />
                <Route path="/guide" element={
                  <ProtectedRoute>
                    <Guide />
                  </ProtectedRoute>
                } />
                <Route path="/groups" element={
                  <ProtectedRoute>
                    <Groups />
                  </ProtectedRoute>
                } />
                <Route path="/members" element={
                  <ProtectedRoute>
                    <Members />
                  </ProtectedRoute>
                } />
                <Route path="/categories" element={
                  <ProtectedRoute>
                    <Categories />
                  </ProtectedRoute>
                } />
                <Route path="/export" element={
                  <ProtectedRoute>
                    <Export />
                  </ProtectedRoute>
                } />
                <Route path="/subscription" element={
                  <ProtectedRoute>
                    <Subscription />
                  </ProtectedRoute>
                } />
                <Route path="/settings" element={
                  <ProtectedRoute>
                    <SettingsPage />
                  </ProtectedRoute>
                } />
                {/* Marketing Route - MegaReach Integration */}
                <Route path="/marketing" element={
                  <ProtectedRoute>
                    <Marketing />
                  </ProtectedRoute>
                } />
                {/* Referral Program Route */}
                <Route path="/referral" element={
                  <ProtectedRoute>
                    <ReferralProgram />
                  </ProtectedRoute>
                } />
                {/* Admin Route - Protected and Admin Only */}
                <Route path="/admin" element={
                  <ProtectedRoute adminOnly={true}>
                    <AdminDashboard />
                  </ProtectedRoute>
                } />
              </Routes>
            </MainLayout>
          } />
        </Routes>
      </BrowserRouter>
    </NotificationContext.Provider>
  );
}

function App() {
  // Initialize referral tracking on app load
  useEffect(() => {
    const referralData = initReferralTracking();
    if (referralData.hasReferral) {
      console.log('[App] Referral tracking initialized:', referralData.code);
    }
  }, []);

  return (
    <ThemeProvider>
      <LanguageProvider>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
